/**
 * Billing — Wave 4 native step (resurrected from b931d8142^ over billingSlice).
 *
 * Route: /purchase-flow-revamp/billing
 *
 * Native rebuild of the four billing sub-sections — invoicing-address picker,
 * payment-method radio group, order-reference + property-owner text, CC-emails
 * list — reading/writing the flat `billingSlice` directly via its setters
 * (Kind-A native-UI seam, MODULE-CONTRACT §11). NO legacy `PurchaseFlow/*`
 * imports, NO state bridge, NO legacy provider tree.
 *
 * §11.1 (BINDING): there is NO mount-time write to any persisted slice field.
 * Initial form values are READ from the slice at render; setters fire ONLY on
 * user interaction. Draft resume populates the slice via `hydrateFromDraft`
 * before this page mounts. This kills the `BillingStateBridge` back-nav wipe
 * by construction (§11.6).
 *
 * Data seam: `dataQueries/invoicingAddresses.query.ts` (native React Query
 * hooks, no provider) + `useBillingPaymentMethods` over the v2 instruction
 * options (BE-driven method availability + `isDefault` preselect).
 * Admin path gates on `targetUserId`.
 *
 * Layout: today's shared `StepShell` (top bar + stepper + cart sidebar).
 * Back/Next live in the cart sidebar's `StepNavSection`, so this page owns no
 * footer (the recovered hand-rolled `PfTopBar`/sticky-footer was dropped).
 */

import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'


import { useAuth0 } from 'utils/auth'
import { useUserData } from 'components/contexts/UserDataContext'
import type { PaymentMethod } from 'models/purchaseFlowRevamp'

import { usePfStore } from '../../store'
import { PfRevampEventName, usePfRevampAnalytics } from '../../analytics'
import { useSyncStepWithRoute } from '../../routing/useSyncStepWithRoute'
import { useRequireActiveSession } from '../../routing/useRequireActiveSession'
import { useBeforeUnloadGuard } from '../../routing/useBeforeUnloadGuard'
import { PfPageHeading, PfRetryAlert, StepShell } from '../../components'
import { Stack } from '@mui/material'

import {
  CcEmailsSection,
  OrderDetailsSection,
  InvoicingAddressSection,
  PaymentMethodSection
} from './sections'
import { useBillingPaymentMethods } from './useBillingPaymentMethods'
import { resolveInvoiceOption } from './billing.helpers'
import { resolveDefaultCcEmails, resolveDefaultReference, resolveEffectiveCcEmails } from './billingDefaults'
import { useIsFreeCart } from './chargeable'
import { PfStepPath, usePfPath } from '../../routing/pfBasePath'

/**
 * @page Billing
 * Billing step — Wave 4 native UI inside the shared `StepShell`.
 *
 * @example
 * // Registered in PurchaseFlowRevamp.page.tsx as:
 * <Route path="billing" element={<Billing />} />
 */
export const Billing: React.FC = () => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  const pfPath = usePfPath()
  useRequireActiveSession(pfPath(PfStepPath.CATALOGUE))
  useBeforeUnloadGuard()

  // Store reads (§11.1 — read at render, never seed on mount) ------------------
  const invoicingAddress = usePfStore((s) => s.invoicingAddress)
  const paymentMethod = usePfStore((s) => s.paymentMethod)
  const reference = usePfStore((s) => s.reference)
  const propertyOwner = usePfStore((s) => s.propertyOwner)
  const ccEmails = usePfStore((s) => s.ccEmails)
  const ccEmailsTouched = usePfStore((s) => s.ccEmailsTouched)
  const property = usePfStore((s) => s.property)
  const targetUserId = usePfStore((s) => s.targetUserId)

  // Catalogue-driven availability + the BE-preferred method. Read at
  // RENDER and combined with the slice value; NOTHING is written on mount
  // (§11.1 — `paymentMethod` is persisted + in the autosave fingerprint, so a
  // seeding effect would dirty every draft on every Billing mount/resume).
  const {
    methods,
    offers,
    defaultPaymentMethod,
    isLoading: isMethodsLoading,
    isSettled: isMethodsSettled,
    hasError: optionsFailed,
    refetch: refetchOptions,
  } = useBillingPaymentMethods()
  const effectivePaymentMethod = paymentMethod ?? defaultPaymentMethod

  // Store writes (fire ONLY on user interaction) ------------------------------
  const setInvoicingAddress = usePfStore((s) => s.setInvoicingAddress)
  const setPaymentMethod = usePfStore((s) => s.setPaymentMethod)
  const setReference = usePfStore((s) => s.setReference)
  const setPropertyOwner = usePfStore((s) => s.setPropertyOwner)
  const setCcEmails = usePfStore((s) => s.setCcEmails)
  const setCcEmailsTouched = usePfStore((s) => s.setCcEmailsTouched)

  // Ephemeral CC-email row input — local (not persisted); commit-to-slice
  // happens on Enter / Add.

  // A cart with nothing to pay for asks for no payment method, as v1
  // cleared it for a free cart. The test mirrors the BE's chargeable rule
  // so the step only hides where the BE accepts no method.
  // Boolean, never the raw `Big` — see `isOrderChargeable`'s note.
  const isFreeCart = useIsFreeCart()
  const currency = usePfStore((s) => s.items[0]?.priceSnapshot?.currency)

  useSyncStepWithRoute('billing', {
    invoiceOption: resolveInvoiceOption({ isSettled: isMethodsSettled, hasError: optionsFailed, isFreeCart, offers }),
  })

  const { roles } = useAuth0()
  const trackAnalytics = usePfRevampAnalytics()
  const { hasWorkspaceActiveSubscription, clientData, currentWorkspaceCcEmails } = useUserData()

  // Derived at RENDER and threaded to the wire, never seeded
  // into the slice (§11.1). The same contract the payment-method default uses.
  const defaultReference = useMemo(() => resolveDefaultReference(property), [property])
  const defaultCcEmails = useMemo(
    () => resolveDefaultCcEmails(clientData?.ccEmails, currentWorkspaceCcEmails),
    [clientData?.ccEmails, currentWorkspaceCcEmails]
  )
  const effectiveCcEmails = useMemo(
    () => resolveEffectiveCcEmails(ccEmails, ccEmailsTouched, defaultCcEmails),
    [ccEmails, ccEmailsTouched, defaultCcEmails]
  )

  // Any add or remove makes the list the client's own, so clearing it sticks.
  const handleCcEmailsChange = useCallback((next: string[]) => {
    setCcEmailsTouched(true)
    trackAnalytics({
      type: PfRevampEventName.CC_EMAIL_CHANGED,
      payload: { added: next.length > effectiveCcEmails.length },
    })
    setCcEmails(next)
  }, [setCcEmails, setCcEmailsTouched, trackAnalytics, effectiveCcEmails.length])

  const handleMethodPicked = useCallback((method: PaymentMethod) => {
    trackAnalytics({
      type: PfRevampEventName.PAYMENT_METHOD_SELECTED,
      payload: { paymentMethod: method, isSubscribed: !!hasWorkspaceActiveSubscription },
    })
  }, [trackAnalytics, hasWorkspaceActiveSubscription])

  return (
    <StepShell>
      <PfPageHeading
        title={t('purchase_flow_revamp:billing.page_title')}
        description={t('purchase_flow_revamp:billing.page_subtitle')}
      />

      <Stack gap={2.4} data-cy="pf-revamp-billing">

        {/* Legacy wrapped this whole step in `DynamicQueryContent` with a
            retry (`onPurge`). v2 kept working on a failed options call but said
            nothing, so the payment methods on screen are the hardcoded fallback
            three and may not be what the org can actually use. */}
        {optionsFailed &&
          <PfRetryAlert
            data-cy="pf-revamp-billing-options-error"
            message={t('purchase_flow_revamp:billing.options_error')}
            onRetry={refetchOptions}
          />
        }

        {/* First on the page: the order reference is what the client recognises
            the order by afterwards (it becomes the order title in the
            dashboard), so it is asked before the billing mechanics. */}
        <OrderDetailsSection
          reference={reference ?? defaultReference}
          propertyOwner={propertyOwner}
          onReferenceChange={setReference}
          onPropertyOwnerChange={setPropertyOwner}
        />

        <InvoicingAddressSection
          value={invoicingAddress}
          onChange={setInvoicingAddress}
          targetUserId={targetUserId}
        />

        {/* A free cart has nothing to pay for, so it is not asked. Legacy
            gated its whole payment box on `total.totalAfterDiscount.gt(0)` and
            actively cleared the method. The wire still needs one until the BE
            makes `billingMethod` optional (ISSUE-3107). */}
        {!isFreeCart &&
          <PaymentMethodSection
            value={effectivePaymentMethod}
            onChange={setPaymentMethod}
            methods={methods}
            offers={offers}
            currency={currency}
            canSaveDefault={!!roles.isClient}
            onMethodPicked={handleMethodPicked}
            isLoading={isMethodsLoading}
          />
        }

        <CcEmailsSection
          value={effectiveCcEmails}
          onChange={handleCcEmailsChange}
        />

      </Stack>
    </StepShell>
  )
}
